/**
 * Protected Route - Require authentication
 */

import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, type OrgLookupResult } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

type OrganizationAccessStatus = Exclude<OrgLookupResult['status'], 'ok'> | null;

function loginDestination(pathname: string, search: string, hash: string): string {
  const returnTo = `${pathname}${search}${hash}`;
  return `/login?returnTo=${encodeURIComponent(returnTo)}`;
}

interface OrganizationAccessCopy {
  title: string;
  body: string;
  showRetry: boolean;
}

function organizationAccessCopy(status: OrganizationAccessStatus): OrganizationAccessCopy {
  if (status === 'error') {
    return {
      title: 'Access check failed',
      body: 'We could not complete your organization access check. This may be a temporary connection problem — try again, or sign out and back in.',
      showRetry: true,
    };
  }
  if (status === 'ambiguous') {
    return {
      title: 'Multiple organizations found',
      body: 'Your account belongs to more than one organization, so access cannot be determined safely. Contact an administrator to resolve this.',
      showRetry: false,
    };
  }
  return {
    title: 'Access unavailable',
    body: 'Your organization access could not be verified. Sign out, then contact an administrator before trying again.',
    showRetry: false,
  };
}

function OrganizationAccessUnavailable({
  status,
  signOut,
  onRetry,
}: {
  status: OrganizationAccessStatus;
  signOut: () => Promise<void>;
  onRetry: () => Promise<void>;
}) {
  const copy = organizationAccessCopy(status);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <section
        className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-elevation-1"
        role="alert"
        aria-labelledby="organization-access-title"
      >
        <h1 id="organization-access-title" className="text-xl font-semibold text-foreground">
          {copy.title}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {copy.body}
        </p>
        <div className="mt-5 flex items-center justify-center gap-3">
          {copy.showRetry && (
            <Button type="button" onClick={() => void onRetry()}>
              Try again
            </Button>
          )}
          <Button
            type="button"
            variant={copy.showRetry ? 'outline' : 'default'}
            onClick={() => void signOut()}
          >
            Sign out
          </Button>
        </div>
      </section>
    </main>
  );
}

function AdminAccessRequired() {
  const navigate = useNavigate();

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <section
        className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-elevation-1"
        role="alert"
        aria-labelledby="admin-access-title"
      >
        <h1 id="admin-access-title" className="text-xl font-semibold text-foreground">
          Admin access required
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This area is limited to administrators. Contact an administrator if you need access.
        </p>
        <Button className="mt-5" type="button" onClick={() => navigate('/')}>
          Back to Mission Control
        </Button>
      </section>
    </main>
  );
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, loading, organizationId, organizationStatus, signOut, retryOrganizationLookup } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!session) {
    return (
      <Navigate
        to={loginDestination(location.pathname, location.search, location.hash)}
        replace
      />
    );
  }
  if (!organizationId) {
    return (
      <OrganizationAccessUnavailable
        status={organizationStatus === 'ok' ? null : organizationStatus}
        signOut={signOut}
        onRetry={retryOrganizationLookup}
      />
    );
  }

  return <>{children}</>;
}

export function AdminRoute({ children }: { children: React.ReactNode }) {
  const {
    session,
    loading,
    organizationId,
    organizationStatus,
    isAdmin,
    signOut,
    retryOrganizationLookup,
  } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }
  if (!session) {
    return (
      <Navigate
        to={loginDestination(location.pathname, location.search, location.hash)}
        replace
      />
    );
  }
  if (!organizationId) {
    return (
      <OrganizationAccessUnavailable
        status={organizationStatus === 'ok' ? null : organizationStatus}
        signOut={signOut}
        onRetry={retryOrganizationLookup}
      />
    );
  }
  if (!isAdmin) return <AdminAccessRequired />;
  return <>{children}</>;
}
